import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Form,
  message,
  Modal,
  notification,
  Row,
  Space,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  FileTextOutlined,
  RollbackOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import moment from 'moment/moment';
import { BASE_URI, downloadFiles, SERVICE_URI } from '../../../services/files';
import { updateDocExpDate } from '../../../services/orders';

import { postFiles } from '../../../helpers/api';
import Uploader from './Uploader';
import { ORDER_STATUS } from '../../../pages/orders/constants';
import DatePickerLocale from '../../DatePickerLocale';
import { ALLOWED_FILE_TYPES } from '../../../helpers/constants';

const ProductModalDocUpload = ({ props }) => {
  const { t } = useTranslation();
  const [uploadForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const {
    dt,
    orderId,
    disabled,
    name,
    setValue,
    currentDoc,
    status,
    customerType,
    setFormValue,
    getValue,
  } = props;
  const [file, setFile] = useState(null);
  const [requiredFileUploaded, setRequiredFileUploaded] = useState(false);

  useEffect(() => {
    uploadForm.setFieldsValue({
      uploadFile: {
        expirationDate: currentDoc
          ? currentDoc.expirationDate && moment(currentDoc.expirationDate)
          : null,
      },
    });
  }, [uploadForm, currentDoc]);

  const isFilled = requiredFileUploaded || (currentDoc && currentDoc.fileId);

  return (
    <Space>
      <Button
        type={isFilled ? 'primary' : 'danger'}
        htmlType="button"
        disabled={disabled}
        onClick={() => setIsModalVisible(true)}
        icon={<UploadOutlined />}
        style={
          disabled && isFilled
            ? {
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: '#fff',
                cursor: 'not-allowed',
              }
            : {}
        }
      />

      <Modal
        title={dt.name}
        visible={isModalVisible}
        closable={false}
        footer={
          <Space>
            <Button
              className="ant-btn-success"
              type="primary"
              htmlType="button"
              icon={<CheckOutlined />}
              onClick={() => {
                if (ORDER_STATUS.SELECT_PRODUCTS !== status) {
                  const { expirationDate } =
                    uploadForm.getFieldValue('uploadFile');
                  if (ORDER_STATUS.DOC_VALIDATION === status) {
                    const updateDocumentDto = {
                      fileId: currentDoc.fileId,
                      expirationDate,
                    };
                    updateDocExpDate(updateDocumentDto).then(() => {
                      setValue(name, currentDoc, expirationDate, true);
                      setIsModalVisible(false);
                    });
                  } else {
                    setValue(name, currentDoc, expirationDate, true);
                    setIsModalVisible(false);
                  }
                } else {
                  uploadForm
                    .validateFields()
                    .then(() => {
                      setIsModalVisible(false);
                      const { expirationDate } =
                        uploadForm.getFieldValue('uploadFile');
                      const files = [];
                      files.push(file);
                      postFiles(`${SERVICE_URI}${BASE_URI}`, files)
                        .then((res) => res.json())
                        .then((res) => {
                          setValue(name, res, expirationDate, false);
                          setFormValue('docMap', {
                            ...getValue('docMap'),
                            [res[0].id]: file,
                          });
                        })
                        .then(() => {
                          uploadForm.resetFields();
                        })
                        .then(() => {
                          setRequiredFileUploaded(true);
                        })
                        .catch(() => console.log('error'));
                    })
                    .catch(() =>
                      notification.error({
                        message: t('errors.validationCriteriaMismatch'),
                      }),
                    );
                }
              }}
            >
              {t('actions.accept')}
            </Button>
            <Button
              className="ant-btn-primary"
              type="primary"
              htmlType="button"
              icon={<RollbackOutlined />}
              onClick={() => setIsModalVisible(false)}
            >
              {t('actions.cancel')}
            </Button>
          </Space>
        }
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name={['uploadFile', 'expirationDate']}
            rules={[
              {
                required: true,
                message: t('errors.selectDate'),
              },
            ]}
          >
            <DatePickerLocale
              format="DD.MM.YYYY HH:mm"
              disabled={customerType}
              showTime
              style={{ width: '100%' }}
              initialValues={moment()}
              disabledDate={(current) => {
                return current < moment();
              }}
            />
          </Form.Item>
          <Row justify="space-around" align="top">
            <Col span={22} push={2}>
              {ORDER_STATUS.SELECT_PRODUCTS === status && (
                <Form.Item
                  name={['uploadFile', 'file']}
                  rules={[
                    {
                      required: true,
                      message: t('actions.selectFile'),
                    },
                  ]}
                >
                  <Uploader
                    props={{
                      names: ['uploadFile', 'file'],
                      handleFileUploaded: (selectedFile) => {
                        if (selectedFile) {
                          if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
                            message.error({
                              content: t('errors.invalidFileType'),
                              duration: 2.5,
                              className: 'card-message',
                            });
                            return;
                          }
                          const convertBytesToMB = (bytes) =>
                            bytes / (1024 * 1024);
                          const maxSize = process.env.MAX_FILE_SIZE || 5;
                          if (convertBytesToMB(selectedFile.size) > maxSize) {
                            message.error({
                              content: t('errors.maxFileSizeReached', {
                                fileName: selectedFile.name,
                                maxSize,
                              }),
                              duration: 2.5,
                              className: 'card-message',
                            });
                          } else {
                            setFile(selectedFile);
                          }
                        }
                      },
                    }}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={2} pull={22} style={{ top: 10 }}>
              {orderId !== null && orderId !== undefined && (
                <Space style={{ paddingLeft: '1rem' }}>
                  <Tooltip title={t('actions.orders.viewDoc')}>
                    <Button
                      shape="circle"
                      type="link"
                      icon={<FileTextOutlined />}
                      style={{ color: '#24ad09' }}
                      onClick={() => downloadFiles(currentDoc.fileId, orderId)}
                    />
                  </Tooltip>
                </Space>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};
export default ProductModalDocUpload;
